import React from "react";

/* -------------------- Types -------------------- */

type Bubble = {
  label?: string;
  colors: string;
  color: string;
  text: string;
};

type CardProps = {
  tag: string;
  tagClass: string;
  iconClass: string;
  iconSymbol: React.ReactNode;
  boxClass: string;
  cardClass: string;
  animateTag?: boolean;
  bubbles: Bubble[];
  footerText: string;
  footerClass: string;
};

/* -------------------- Card Component -------------------- */

const Card: React.FC<CardProps> = ({
  tag,
  tagClass,
  iconClass,
  iconSymbol,
  boxClass,
  cardClass,
  animateTag = false,
  bubbles,
  footerText,
  footerClass,
}) => {
  return (
    <div
      className="relative pt-[28px] w-[400px]"
      style={{
        animation: "cardSlideIn 0.55s cubic-bezier(0.22,1,0.36,1) forwards",
      }}
    >
      {/* Tag */}
      <div
        className={`absolute top-2 left-[-14px] inline-flex items-center gap-3 rounded-[8px] px-[16px] py-[5px] sm:text-base text-sm font-semibold z-[4] whitespace-nowrap ${tagClass} font-primary`}
        style={{
          animation: animateTag
            ? "popBounce 1.8s cubic-bezier(0.22,1,0.36,1) infinite"
            : "none",
          transformOrigin: "center bottom",
        }}
      >
        <div
          className={`w-[18px] h-[18px] rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0 ${iconClass}`}
        >
          {iconSymbol}
        </div>
        {tag}
      </div>

      {/* Card */}
      <div
        className={`bg-white rounded-2xl sm:px-[22px] px-[10px] pt-12 pb-[20px] text-left sm:h-[230px] h-[240px] ${cardClass} ${boxClass}`}
      >
        <div className="flex flex-col gap-[14px] mb-[18px]">
          {bubbles.map((b, i) => (
            <div key={i}>
              {b.label && (
                <div className="text-[12px] text-text-gray mb-[6px] font-medium font-primary italic">
                  {b.label}
                </div>
              )}

              <div
                className="relative inline-block rounded-[8px] px-[14px] py-[9px] font-semibold font-primary sm:text-sm text-[12px] text-[#faf8fc]"
                style={{
                  background: b.color,
                  boxShadow: `3px 3px 0 0 ${b.colors}`,
                }}
              >
                {b.text}

                {/* Tail */}
                <div
                  className="absolute -top-[6px] left-[-1px] w-2 h-2"
                  style={{
                    borderLeft: "10px solid transparent",
                    rotate: "75deg",
                    borderRight: "0px solid transparent",
                    borderBottom: `10px solid ${b.color}`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className={`sm:text-base text-sm font-primary italic ${footerClass}`}>
          {footerText}
        </div>
      </div>
    </div>
  );
};

/* -------------------- Main Component -------------------- */

const Translation: React.FC = () => {
  return (
    <div className=" bg-[#faf8fc] flex justify-center items-center min-h-screen sm:px-12 px-6 sm:pt-24 pt-12 pb-12">
      <div className="w-full text-center">
        <h1 className="lg:text-5xl sm:text-4xl text-3xl font-extrabold text-[#0f172a] mb-6 font-primary">
          Translation that actually
          <span className="text-[#059669]"> understands.</span>
        </h1>

        <p className="sm:text-base text-sm text-[#64748B] max-w-[550px] mx-auto font-primary">
          Most apps translate word-by-word. Vyzle uses context-aware translation
          that understands slang, idioms, and cultural nuance — so your message
          lands the way you meant it.
        </p>

        <div className="flex gap-14 justify-center flex-wrap items-start mt-10 mb-4">
          <Card
            tag="Other Apps"
            animateTag={false}
            iconSymbol="−"
            tagClass="bg-[#E84B4B] text-[#faf8fc] border border-[#E84B4B] shadow-[4px_4px_0_0_#AC3939]"
            boxClass="shadow-[6px_6px_0_0_#AC3939]"
            iconClass="bg-[#f8fafc] text-[#E84B4B]"
            cardClass="border border-[#E84B4B]"
            bubbles={[
              {
                colors: "#B85B2B",
                color: "#FF803E",
                text: 'English: "That test was a piece of cake"',
              },
              {
                colors: "#AC3A3A",
                label: "Translated",
                color: "#E84B4B",
                text: 'Spanish: "Esa prueba fue un pedazo de pastel"',
              },
            ]}
            footerText="Literal - Makes no sense"
            footerClass="text-[#E84B4B]"
          />

          <Card
            tag="Vyzle"
            iconSymbol="✓"
            animateTag={true}
            tagClass="bg-[#059669] text-[#faf8fc] border border-[#059669] shadow-[4px_4px_0_0_#01573C]"
            boxClass="shadow-[6px_6px_0_0_#01573C]"
            iconClass="bg-[#f8fafc] text-[#059669]"
            cardClass="border border-[#059669]"
            bubbles={[
              {
                colors: "#573D92",
                color: "#6A35E0",
                text: 'English: "That test was a piece of cake"',
              },
              {
                colors: "#01573C",
                label: "Translated",
                color: "#059669",
                text: 'Spanish: "Ese examen fue muy fácil"',
              },
            ]}
            footerText="Context-aware — natural and correct"
            footerClass="text-[#059669]"
          />
        </div>

        <p className="mt-[40px] sm:text-base text-sm text-[#64748B] font-primary">
          "Don't let translation turn you into a robot—keep your personality across every border."
        </p>
      </div>
    </div>
  );
};

export default Translation;